import {TitleCasePipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {BehaviorSubject, Subscription} from 'rxjs';

import {ProjectAlert} from 'src/app/core/model/project';
import {CreateAlertAction, DeleteAlertAction, UpdateAlertAction} from 'src/app/core/store/projects/projects.action';
import {MessageService} from 'src/app/services/message.service';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphButtonModule} from '../../design/oph-button/oph-button.module';
import {OphIconModule} from '../../design/oph-icon/oph-icon.module';
import {OphInputsModule} from '../../design/oph-inputs/oph-inputs.module';
import {OphTimeSelectorComponent} from '../../design/oph-time-selector/oph-time-selector.component';
import {OphXButtonComponent} from '../../design/oph-x-button/oph-x-button.component';
import {PipesModule} from '../../pipes/pipes.module';
import {TasksService} from '../../services/tasks.service';
import {TaskIconPickerButtonComponent} from '../../tasks/icon-picker-button/task-icon-picker-button.component';
import {TaskTriggerSelectionComponent} from '../../tasks/trigger-selection/task-trigger-selection.component';
import {NewTrackerInDialogComponent} from '../../trackers/new-tracker-in-dialog/new-tracker-in-dialog.component';

interface DialogData {
  alert?: ProjectAlert;
  stepId: string;
  projectId: string;
}

@Component({
  selector: 'alerts-dialog',
  standalone: true,
  imports: [
    MatMenuModule,
    PipesModule,
    OphInputsModule,
    OphIconModule,
    OphXButtonComponent,
    TaskTriggerSelectionComponent,
    OphButtonModule,
    MatIconModule,
    TitleCasePipe,
    OphTimeSelectorComponent,
    NewTrackerInDialogComponent,
    TaskIconPickerButtonComponent,
  ],
  templateUrl: './alerts-dialog.component.html',
  styleUrls: ['./alerts-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertsDialogComponent implements OnInit, OnDestroy {
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);

  form: FormGroup;
  view$ = new BehaviorSubject<'form' | 'icon'>('form');
  repeatOptions = Array.from({length: 10}, (_, i) => i + 1);
  repeatTypeOptions = [
    {value: 'day', label: 'Day'},
    {value: 'week', label: 'Week'},
  ];
  private subscription = new Subscription();
  scheduleDisabled = false;

  icons = [
    'alien',
    'bell',
    'exclamation',
    'folded-hands',
    'ghost',
    'hourglass',
    'magnify-glass',
    'poo',
    'rock',
    'skull',
    'smiley',
    'sparkles',
    'thumbs-down',
    'thumbs-up',
  ];

  startTimeForm = new FormGroup({
    hours: new FormControl<string>('12'),
    minutes: new FormControl<string>('00'),
    ampm: new FormControl<string>('AM'),
  });

  showTimeSelector = false;
  loadingSave = false;
  loadingDelete = false;
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AlertsDialogComponent>,
    private timezoneService: TimezoneService,
    private store$: Store,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private tasksService: TasksService
  ) {}

  ngOnInit() {
    const alert = this.data?.alert;
    this.initializeForm(alert);

    // Subscribe to trigger section changes
    this.subscription.add(
      this.form.get('trigger').valueChanges.subscribe(trigger => {
        this.scheduleDisabled = this.tasksService.checkIfTriggerIsComplete(trigger);
        this.updateScheduleValidators();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  initializeForm(alert?: ProjectAlert) {
    this.form = this.fb.group({
      active: [alert?.active || true],
      icon: [alert?.icon || 'bell', Validators.required],
      name: [alert?.name || '', Validators.required],
      alertText: [alert?.alertText || '', [Validators.required, Validators.maxLength(220)]],
      schedule: this.fb.group({
        repeatType: [alert?.schedule?.repeatType || 'week'],
        repeatEvery: [alert?.schedule?.repeatEvery || 1],
        repeatDays: [alert?.schedule?.repeatDays || []],
        alertStart: [alert?.schedule?.alertStart || ''],
        timezone: [this.timezoneService.getTimeZone()],
      }),
      trigger: this.fb.group({
        trackerId: [alert?.trigger?.trackerId || ''],
        metricId: [alert?.trigger?.metricId || ''],
        operator: [alert?.trigger?.operator || 'eq'],
        value: [alert?.trigger?.value || ''],
      }),
    });

    // Set initial scheduleDisabled value for pre-filled forms
    const trigger = this.form.get('trigger').value;
    this.scheduleDisabled = this.tasksService.checkIfTriggerIsComplete(trigger);

    // Update schedule validators based on initial trigger state
    this.updateScheduleValidators();
  }

  // Method to update schedule validators based on trigger completeness
  private updateScheduleValidators() {
    const scheduleGroup = this.form.get('schedule') as FormGroup;
    const trigger = this.form.get('trigger').value;
    const isTriggerComplete = this.tasksService.checkIfTriggerIsComplete(trigger);

    const scheduleControls = ['repeatType', 'repeatEvery', 'repeatDays', 'alertStart'];

    scheduleControls.forEach(controlName => {
      const control = scheduleGroup.get(controlName);

      if (isTriggerComplete) {
        // If trigger is complete, schedule is optional
        control.clearValidators();
      } else {
        // If trigger is not complete, schedule is required
        control.setValidators(Validators.required);
      }

      control.updateValueAndValidity();
    });
  }

  onIconSelect(icon: string) {
    this.form.patchValue({icon});
    this.view$.next('form');
  }

  onCancel() {
    this.dialogRef.close();
  }

  onFinish() {
    if (this.form.valid) {
      const alert = {...this.data?.alert, ...this.form.value};
      this.loadingSave = true;
      if (this.data?.alert) {
        this.store$.dispatch(
          new UpdateAlertAction({
            alert,
            stepId: this.data.stepId,
            projectId: this.data.projectId,
            onSuccess: () => this.onCreateAlertSuccess(),
            onFailure: error => this.onCreateAlertFailure(error),
          })
        );
      } else {
        this.store$.dispatch(
          new CreateAlertAction({
            alert,
            stepId: this.data.stepId,
            projectId: this.data.projectId,
            onSuccess: () => this.onCreateAlertSuccess(),
            onFailure: error => this.onCreateAlertFailure(error),
          })
        );
      }
    }
  }

  onCreateAlertSuccess() {
    this.dialogRef.close();
    this.loadingSave = false;
  }

  onCreateAlertFailure(error: HttpErrorResponse) {
    this.messageService.add(error.message || 'There was a problem with the alert', 'error');
    this.loadingSave = false;
  }

  onIcon(icon: string) {
    this.form.patchValue({icon});
    this.view$.next('form');
  }

  onAlertTextChange(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.form.get('alertText').setValue(value);
  }

  onTimeButtonClick() {
    if (this.data?.alert?.schedule?.alertStart) {
      const alertStartMoment = moment(this.data.alert.schedule.alertStart).tz(this.timezoneService.getTimeZone());
      const hours = alertStartMoment.hours();
      const displayHours = hours % 12 === 0 ? 12 : hours % 12;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const minutes = alertStartMoment.minutes();

      this.startTimeForm.patchValue({
        hours: displayHours.toString(),
        minutes: minutes.toString().padStart(2, '0'),
        ampm,
      });
      this.cdr.markForCheck();
    }
    this.showTimeSelector = true;
  }

  closeTimeSelector() {
    const hours = +this.startTimeForm.value.hours;
    const adjustedHours =
      this.startTimeForm.value.ampm === 'PM' ? (hours === 12 ? 12 : hours + 12) : hours === 12 ? 0 : hours;

    const alertStart = moment()
      .tz(this.timezoneService.getTimeZone())
      .set('hours', adjustedHours)
      .set('minutes', +this.startTimeForm.value.minutes)
      .set('seconds', 0)
      .utc()
      .format();

    this.form.patchValue({schedule: {alertStart}});
    this.showTimeSelector = false;
  }

  onRemoveTrigger() {
    this.form.patchValue({
      trigger: {trackerId: null, metricId: null, operator: 'eq', value: null},
    });
  }

  onCloseNewTrackerInDialog(trackerId: string) {
    if (trackerId) {
      this.form.patchValue({trigger: {trackerId}});
    }
    this.view$.next('form');
  }

  onDelete() {
    this.loadingDelete = true;
    this.store$.dispatch(
      new DeleteAlertAction({
        alertId: this.data.alert._id,
        projectId: this.data.projectId,
        onSuccess: () => this.onDeleteSuccess(),
        onFailure: error => this.onDeleteFailure(error),
      })
    );
  }

  onDeleteSuccess() {
    this.dialogRef.close();
    this.loadingDelete = false;
  }

  onDeleteFailure(error: HttpErrorResponse) {
    this.messageService.add(error.message || 'There was a problem removing the alert', 'error');
    this.loadingDelete = false;
  }

  onRepeatTypeChange(value: string) {
    this.form.get('schedule.repeatType').setValue(value);

    const repeatDaysControl = this.form.get('schedule.repeatDays');

    if (value === 'day') {
      // Set all weekdays (0-6 representing Sunday-Saturday)
      repeatDaysControl.setValue([0, 1, 2, 3, 4, 5, 6]);
    } else {
      // For any other type (e.g., 'day'), clear the repeatDays array
      repeatDaysControl.setValue([]);
    }
  }
}
